import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Users as UsersIcon } from "lucide-react";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [password, setPassword] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.name || !editing?.email) return toast.error("Nome ed email obbligatori");
    try {
      if (editing.id) {
        const payload = { name: editing.name, role: editing.role, active: editing.active };
        if (password) payload.password = password;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        if (!password) return toast.error("Password obbligatoria");
        await api.post("/users", { name: editing.name, email: editing.email, role: editing.role, active: editing.active, password });
      }
      toast.success("Salvato"); setOpen(false); setEditing(null); setPassword(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => {
    if (!confirm("Eliminare l'utente?")) return;
    await api.delete(`/users/${id}`); toast.success("Eliminato"); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Amministrazione</div>
          <h1 className="font-display text-3xl font-bold text-[#0F2C59]">Utenti</h1>
          <p className="text-slate-500">Gestisci accessi e ruoli.</p>
        </div>
        <Button onClick={() => { setEditing({ name: "", email: "", role: "dipendente", active: true }); setPassword(""); setOpen(true); }} className="bg-[#0F2C59]" data-testid="new-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo utente
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600 border-b border-slate-300">
            <tr>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Ruolo</th>
              <th className="text-center px-4 py-2.5">Stato</th>
              <th className="text-right px-4 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-300">
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${u.active ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                    {u.active ? "Attivo" : "Disattivo"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    <button
                      onClick={() => { setEditing({ ...u }); setPassword(""); setOpen(true); }}
                      className="p-1.5 rounded border border-slate-200 bg-white hover:bg-blue-50 hover:border-[#0F2C59] text-[#0F2C59] transition-colors"
                      title="Modifica utente / cambia password"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(u.id)}
                      className="p-1.5 rounded border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 text-red-500 transition-colors"
                      title="Elimina utente"
                      data-testid={`del-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome e cognome</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" data-testid="user-name" /></div>
              <div><Label>Email</Label><Input type="email" disabled={!!editing.id} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1.5" data-testid="user-email" /></div>
              <div><Label>Password {editing.id && "(lascia vuoto per non modificare)"}</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="user-password" /></div>
              <div>
                <Label>Ruolo</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="direzione">Direzione</SelectItem>
                    <SelectItem value="commerciale">Commerciale</SelectItem>
                    <SelectItem value="dipendente">Dipendente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <span className="text-sm">Utente attivo</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2C59]" data-testid="save-user-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
